/**
 * Canonical region → address form config.
 *
 * SRP: THE single source of truth for "how should we label + validate an
 * address form for region X". Everything address-related on the FE — checkout
 * billing form, delivery address form, provider settings, shop-setup — reads
 * from here.
 *
 * OCP: to add a new region, add one entry to `ADDRESS_FORMATS` — no view code
 * changes. Mirrors the backend's `Zaveato.Domain.Common.RegionDefaults`.
 *
 * Fallback: unknown / null region → GB config, matching BE `FallbackRegion`.
 * That way a shop with a missing `regionCode` renders as UK (the safest
 * default given every legacy row is UK).
 */
import type { RegionCode } from '@/config/hosts'

export interface AddressFormat {
  /** Field label above the postcode input (regional term: UK "Postcode", PK "Postal Code"). */
  postcodeLabel: string
  /** Placeholder inside the postcode input showing the local format. */
  postcodePlaceholder: string
  /** Uppercase-transform the postcode as the user types (UK convention). */
  postcodeUppercase: boolean
  /** Optional client-side regex for structural sanity check. `null` = don't validate. */
  postcodePattern: RegExp | null
  /** Whether the postcode input is required at submit. GB: yes (postcode-lookup gates delivery zone); PK: no (rural areas often have no postal code, city + province is enough). */
  postcodeRequired: boolean
  /**
   * True when this region has a postcode → (lat/lng, city) geocoder wired
   * on the FE (today: GB via postcodes.io). Callers that need customer
   * coords branch on this flag rather than hardcoding `region === 'GB'`:
   *   - `useUserLocations.detectCurrentLocation` — GB enriches browser
   *     geolocation with a canonical UK postcode; other regions keep raw
   *     browser coords.
   *   - `CheckoutView` — GB gets coords from the postcode lookup; other
   *     regions fall back to the home-page LocationSelector's saved coords.
   *   - `ShopCheckoutService` (BE mirror) applies the same policy server-side.
   * Add a region-specific geocoder later by wiring its service into
   * `postcodeService.ts` and flipping this flag to `true`.
   */
  postcodeGeocoderAvailable: boolean
  /**
   * Where the postcode input renders relative to the street-address lines.
   *   - `false` (default) — postcode FIRST, then Address Line 1 → 2 → City.
   *     Right for regions with a postcode → address geocoder (GB via
   *     postcodes.io) since typing the postcode is the fastest path to
   *     an auto-filled address.
   *   - `true` — Address Line 1 → 2 → City → Province → postcode LAST.
   *     Right for regions where postcodes are optional and don't drive
   *     lookup (PK/AE/SA), because customers there physically write the
   *     address first and postcode is a footnote.
   * Deliberately separate from `postcodeGeocoderAvailable` — the
   * geocoder decision controls submit validation + coord resolution;
   * this one controls form layout. Two orthogonal concerns.
   */
  postcodeAfterAddressLines: boolean
  /** Field label above the sub-national administrative area (UK "County", PK "Province"). */
  regionLabel: string
  /** Placeholder shown inside the region input. */
  regionPlaceholder: string
  /** Render the region-label input on address forms. GB hides it (County is optional and rarely used); PK/AE/SA show it. */
  showRegionInput: boolean
  /** Field label above the city input. Universally "City" today but here for symmetry. */
  cityLabel: string
  /** ISO 3166-1 alpha-2 country code for Stripe / third-party APIs. */
  countryCode: string
  /** Human-readable country name shown to the user + persisted on the order. */
  countryName: string
}

const GB: AddressFormat = {
  postcodeLabel: 'Postcode',
  postcodePlaceholder: 'e.g. M14 5LB',
  postcodeUppercase: true,
  postcodePattern: /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i,
  postcodeRequired: true,
  postcodeGeocoderAvailable: true,
  // GB postcode drives the address lookup — must render FIRST so the
  // customer sees the auto-filled Address Line 1 / City appear as they type.
  postcodeAfterAddressLines: false,
  regionLabel: 'County',
  regionPlaceholder: 'e.g. Greater Manchester',
  // County is optional + rarely typed in the UK — leave it off by default so
  // the checkout stays uncluttered. If a UK provider wants it exposed later,
  // flip this without touching any view code.
  showRegionInput: false,
  cityLabel: 'City',
  countryCode: 'GB',
  countryName: 'United Kingdom',
}

const PK: AddressFormat = {
  postcodeLabel: 'Postal Code',
  postcodePlaceholder: 'e.g. 54000',
  postcodeUppercase: false,
  postcodePattern: /^\d{5}$/,
  // Rural + peri-urban Pakistan often lacks a formal postal code; the
  // shop/rider identifies deliveries by city + province + landmark instead.
  // Making it mandatory would block legitimate orders.
  postcodeRequired: false,
  postcodeGeocoderAvailable: false,
  // PK addresses are written street-first — postcode is a footnote, not
  // a lookup key. Render it at the BOTTOM after Address / City / Province.
  postcodeAfterAddressLines: true,
  regionLabel: 'Province',
  regionPlaceholder: 'e.g. Punjab',
  showRegionInput: true,
  cityLabel: 'City',
  countryCode: 'PK',
  countryName: 'Pakistan',
}

const AE: AddressFormat = {
  postcodeLabel: 'PO Box',
  postcodePlaceholder: 'e.g. 12345',
  postcodeUppercase: false,
  postcodePattern: null,
  // UAE addresses use PO Box or Makani — the country has no residential
  // postal-code system, so the input is optional.
  postcodeRequired: false,
  postcodeGeocoderAvailable: false,
  // UAE addresses use PO Box / Makani — the "postcode" field is a
  // footnote alongside the physical address. Render at the BOTTOM.
  postcodeAfterAddressLines: true,
  regionLabel: 'Emirate',
  regionPlaceholder: 'e.g. Dubai',
  showRegionInput: true,
  cityLabel: 'City',
  countryCode: 'AE',
  countryName: 'United Arab Emirates',
}

const SA: AddressFormat = {
  postcodeLabel: 'Postal Code',
  postcodePlaceholder: 'e.g. 12345',
  postcodeUppercase: false,
  postcodePattern: /^\d{5}$/,
  postcodeRequired: true,
  postcodeGeocoderAvailable: false,
  // SA postcode is required but not a lookup key — written after the
  // physical address like PK / AE. Render at the BOTTOM.
  postcodeAfterAddressLines: true,
  regionLabel: 'Region',
  regionPlaceholder: 'e.g. Riyadh',
  showRegionInput: true,
  cityLabel: 'City',
  countryCode: 'SA',
  countryName: 'Saudi Arabia',
}

export const ADDRESS_FORMATS: Record<RegionCode, AddressFormat> = { GB, PK, AE, SA }

/**
 * Resolve the address form config for a given region code. Case-insensitive,
 * whitespace-tolerant. Unknown / null / empty falls back to GB — matches
 * BE `RegionDefaults.FallbackRegion` so a legacy row without a region renders
 * as UK.
 */
export function getAddressFormat(regionCode: string | null | undefined): AddressFormat {
  const normalised = (regionCode ?? '').trim().toUpperCase() as RegionCode
  return ADDRESS_FORMATS[normalised] ?? GB
}
